import { jwtDecode } from 'jwt-decode';
import { KeyboardIcon, ScanIcon } from 'lucide-react';
import type { GetServerSideProps, NextPage } from 'next';
import Head from 'next/head';
import QrScanner from 'qr-scanner';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import AccessDenied from '@/components/auth/AccessDenied';
import LoginPrompt from '@/components/auth/LoginPrompt';
import CheckpointSelector from '@/components/scanner/CheckpointSelector';
import ScanConfirmationModal from '@/components/scanner/ScanConfirmationModal';
import Scanner from '@/components/scanner/Scanner';
import { auth } from '@/lib/auth';
import evaluateAccess from '@/utils/evaluateAccess';

type ScannerPageProps = {
  isAuthenticated: boolean;
  hasAccess: boolean;
};

export const getServerSideProps: GetServerSideProps<ScannerPageProps> = async (
  context,
) => {
  const session = await auth.api.getSession({
    headers: context.req.headers as HeadersInit,
  });

  const hasAccess =
    !!session?.user.email &&
    (await evaluateAccess(session.user.email, 'scanner'));

  return {
    props: {
      isAuthenticated: !!session,
      hasAccess,
    },
  };
};

const ScannerPage: NextPage<ScannerPageProps> = ({
  isAuthenticated,
  hasAccess,
}) => {
  const [selectedCheckpoint, setSelectedCheckpoint] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [selectedNumber, setSelectedNumber] = useState<number | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'scan' | 'manual'>('scan');
  const { handleSubmit, register, reset } = useForm();

  const onDecode = useMemo(
    () => (result: QrScanner.ScanResult) => {
      if (!selectedCheckpoint) {
        alert('Bitte wählen Sie einen Checkpoint aus');
        return;
      }

      if (!result) return;

      const url = new URL(result.data);
      const token = url.searchParams.get('token');
      if (!token) return;

      const decoded = jwtDecode<{ number: number }>(token);
      if (!decoded) return;

      setSelectedNumber(decoded.number);
      setIsOpen(true);
    },
    [selectedCheckpoint],
  );

  if (!isAuthenticated) {
    return (
      <>
        <Head>
          <title>Scanner</title>
        </Head>
        <div className="flex h-full items-center justify-center">
          <LoginPrompt />
        </div>
      </>
    );
  }

  if (!hasAccess) {
    return (
      <>
        <Head>
          <title>Scanner</title>
        </Head>
        <div className="flex h-full items-center justify-center">
          <AccessDenied />
        </div>
      </>
    );
  }

  return (
    <>
      <Head>
        <title>Scanner</title>
      </Head>

      <div className="mx-auto max-w-lg">
        <CheckpointSelector onChange={setSelectedCheckpoint} />

        <div className="mt-3 flex rounded-xl border border-gray-950/10 bg-gray-50 p-1">
          <button
            type="button"
            onClick={() => setActiveTab('scan')}
            className={`flex flex-1 items-center justify-center gap-2 rounded-lg py-2 text-sm font-medium ${activeTab === 'scan' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}
          >
            <ScanIcon className="size-4" />
            QR-Scanner
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('manual')}
            className={`flex flex-1 items-center justify-center gap-2 rounded-lg py-2 text-sm font-medium ${activeTab === 'manual' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}
          >
            <KeyboardIcon className="size-4" />
            Manuell
          </button>
        </div>

        {activeTab === 'scan' && (
          <div className="mt-3 overflow-hidden rounded-2xl border border-gray-950/10 shadow-sm">
            <Scanner onDecode={onDecode} />
          </div>
        )}

        {activeTab === 'manual' && (
          <div className="mt-3 rounded-xl border border-gray-950/10 bg-white p-5">
            <form
              onSubmit={handleSubmit((data) => {
                setSelectedNumber(data.number);
                reset();
                setIsOpen(true);
              })}
            >
              <div className="space-y-2">
                <Label htmlFor="participant-number">
                  Startnummer <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="participant-number"
                  placeholder="Startnummer"
                  required
                  {...register('number')}
                />
              </div>
              <Button className="mt-4 w-full" type="submit">
                Einchecken
              </Button>
            </form>
          </div>
        )}
      </div>

      {selectedCheckpoint !== null && selectedNumber !== null && (
        <ScanConfirmationModal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          checkpoint={selectedCheckpoint}
          number={selectedNumber}
        />
      )}
    </>
  );
};

export default ScannerPage;
